var _a;
import { setupAddEventListener, checkIfCompatibleWithBackend, } from "@todesktop/client-util";
import { invoke } from "./invoke.js";
const safeWindowToDesktop = typeof window !== "undefined" && window["todesktop"];
export const createOnAndOffNamespaceListeners = (namespace, compatibleWithVersionsAfter) => {
    const on = async (eventName, callback, { ref, preventDefault } = {}) => {
        if (compatibleWithVersionsAfter &&
            !checkIfCompatibleWithBackend(`${namespace} addEventListener`, compatibleWithVersionsAfter)) {
            return;
        }
        const eventId = (await invoke({
            instanceRef: ref,
            namespace,
            eventName,
            eventOptions: {
                preventDefault,
            },
        }));
        // Todo: receiving an electron event as an arugment no longer happens in desktopify and should be removed as a breaking change in future.
        window["todesktop"]["on"](eventId, (eventId, ...args) => {
            // Subs are now invoked in desktopify as subscription(eventId, ...args) instead of subscription(eventId, event, ...args);
            // This means that our subscription handlers needs to check if the second argument is an electron event to make sure we ignore it
            if (isEncapsulatedElectronEvent(args[0])) {
                const [, ...correctArgs] = args;
                return callback(...correctArgs);
            }
            else {
                return callback(...args);
            }
        });
        return () => removeEventListener(eventName, { eventId, ref });
    };
    const removeEventListener = async (eventName, { ref, eventId }) => {
        if (compatibleWithVersionsAfter &&
            !checkIfCompatibleWithBackend(`${namespace} removeEventListener`, compatibleWithVersionsAfter)) {
            return;
        }
        await invoke({
            instanceRef: ref,
            namespace,
            eventName,
            eventId,
        });
        window["todesktop"]["off"](eventId);
    };
    const removeAllListeners = async (eventName, { ref } = {}) => {
        if (compatibleWithVersionsAfter &&
            !checkIfCompatibleWithBackend(`${namespace} removeAllListeners`, compatibleWithVersionsAfter)) {
            return;
        }
        await invoke({
            instanceRef: ref,
            namespace,
            method: "removeAllListeners",
            args: [eventName],
        });
        window["todesktop"]["off"](eventName);
    };
    return {
        removeAllListeners,
        on,
    };
};
function legacyWarning(eventName) {
    // eslint-disable-next-line no-console
    console.warn(`[todesktop-client] ${eventName} is a legacy event and may be removed in future.
        Contact support or look through our typescript files to find a suitable replacement.`);
}
function isLegacyArgs(args, legacyMappings) {
    const eventName = args[0];
    const isLegacy = eventName in legacyMappings;
    if (isLegacy)
        legacyWarning(eventName);
    return isLegacy;
}
const addEventListenerArgsAreLegacy = (args, legacyMappings) => {
    return isLegacyArgs(args, legacyMappings);
};
export const createOnAndOffNamespaceListenersWithCompatiblityForLegacyEvents = (legacyMappings, namespace) => {
    const newApi = createOnAndOffNamespaceListeners(namespace);
    // returns Promise<() => Promise<void>>
    const on = (...args) => {
        if (addEventListenerArgsAreLegacy(args, legacyMappings)) {
            return setupAddEventListener(legacyMappings)(...args);
        }
        return newApi.on(...args);
    };
    return {
        removeAllListeners: newApi.removeAllListeners,
        on,
    };
};
export const isDev = ((_a = safeWindowToDesktop === null || safeWindowToDesktop === void 0 ? void 0 : safeWindowToDesktop.env) === null || _a === void 0 ? void 0 : _a.NODE_ENV) === "dev";
const isEncapsulatedElectronEvent = (val) => {
    return isObject(val) && "preventDefault" in val && "sender" in val;
};
const isObject = (val) => {
    if (val === null) {
        return false;
    }
    return typeof val === "function" || typeof val === "object";
};
